import { Journey, JourneyDailySummary, JourneySummary } from '@dev/ee-api/ee-api-models';

import {
    GetAutomationContactsParams,
    GetAutomationDailySummaryParams,
    GetAutomationsParams,
    ListFromAutomationParams,
} from '@/services/ElasticApi';

import {
    AutomationsActionTypes,
    AutomationsInterface,
    AutomationsPagination,
    AutomationsStatusCounts,
    AutomationStepSummaryAndTemplate,
    ClearPreviewTemplateAction,
    ContactsListPagination,
    DailySummaryPagination,
    DeleteAutomationAction,
    GetAllAutomationsErrorAction,
    GetAllAutomationsStartAction,
    GetAllAutomationsSuccessAction,
    GetAutomationByNameErrorAction,
    GetAutomationByNameStartAction,
    GetAutomationByNameSuccessAction,
    GetAutomationContactsErrorAction,
    GetAutomationContactsStartAction,
    GetAutomationContactsSuccessAction,
    GetAutomationDailySummaryErrorAction,
    GetAutomationDailySummaryStartAction,
    GetAutomationDailySummarySuccessAction,
    ListFromAutomationAction,
    ListFromAutomationErrorAction,
    ListFromAutomationSuccessAction,
    LoadPreviewTemplateAction,
    LoadPreviewTemplateErrorAction,
    LoadPreviewTemplateSuccessAction,
    PickAutomationsAction,
    PreviewTemplate,
    ReactivateAutomationAction,
    UnpickAllAutomationsAction,
    UpdateAutomationStatusAction,
    UpdateAutomationStatusConfirmationAction,
    UpdateAutomationStatusErrorAction,
    UpdateAutomationStatusParams,
    UpdateAutomationStatusSuccessAction,
} from './types';
import { ApplicationState } from '..';

export const GetAllAutomationsStart = (params: GetAutomationsParams): GetAllAutomationsStartAction => ({
    type: AutomationsActionTypes.GET_ALL_AUTOMATIONS_START,
    payload: params,
});

export const GetAllAutomationsSuccess = (
    collection: JourneySummary[],
    statusCounts: AutomationsStatusCounts,
    pagination: AutomationsPagination
): GetAllAutomationsSuccessAction => ({
    type: AutomationsActionTypes.GET_ALL_AUTOMATIONS_SUCCESS,
    payload: {
        collection,
        statusCounts,
        pagination,
    },
});

export const GetAllAutomationsError = (): GetAllAutomationsErrorAction => ({
    type: AutomationsActionTypes.GET_ALL_AUTOMATIONS_ERROR,
});

export const UpdateAutomationStatus = (params: UpdateAutomationStatusParams): UpdateAutomationStatusAction => ({
    type: AutomationsActionTypes.UPDATE_AUTOMATION_STATUS,
    payload: params,
});

export const UpdateAutomationStatusSuccess = (automation: Journey): UpdateAutomationStatusSuccessAction => ({
    type: AutomationsActionTypes.UPDATE_AUTOMATION_STATUS_SUCCESS,
    payload: automation,
});

export const UpdateAutomationStatusError = (): UpdateAutomationStatusErrorAction => ({
    type: AutomationsActionTypes.UPDATE_AUTOMATION_STATUS_ERROR,
});

export const UpdateAutomationStatusConfirmation = (
    params: UpdateAutomationStatusParams
): UpdateAutomationStatusConfirmationAction => ({
    type: AutomationsActionTypes.UPDATE_AUTOMATION_STATUS_CONFIRMATION,
    payload: params,
});

export const ReactivateAutomation = (params: UpdateAutomationStatusParams): ReactivateAutomationAction => ({
    type: AutomationsActionTypes.REACTIVATE_AUTOMATION,
    payload: params,
});

export const DeleteAutomation = (automationName: string[]): DeleteAutomationAction => ({
    type: AutomationsActionTypes.DELETE_AUTOMATIONS,
    payload: {
        Names: automationName,
    },
});

export const PickAutomations = (checkedAutomations: string[]): PickAutomationsAction => ({
    type: AutomationsActionTypes.PICK_AUTOMATIONS,
    payload: {
        checkedAutomations,
    },
});

export const UnpickAllAutomations = (): UnpickAllAutomationsAction => ({
    type: AutomationsActionTypes.UNPICK_AUTOMATIONS,
});

export const GetAutomationByNameStart = (
    name: string,
    isTemplateThumbnailsRequired = false
): GetAutomationByNameStartAction => ({
    type: AutomationsActionTypes.GET_AUTOMATION_BY_NAME_START,
    payload: { name, isTemplateThumbnailsRequired },
});

export const GetAutomationByNameSuccess = (
    singleAutomation: JourneySummary,
    emailsList: AutomationStepSummaryAndTemplate[]
): GetAutomationByNameSuccessAction => ({
    type: AutomationsActionTypes.GET_AUTOMATION_BY_NAME_SUCCESS,
    payload: {
        singleAutomation,
        emailsList,
    },
});

export const GetAutomationByNameError = (): GetAutomationByNameErrorAction => ({
    type: AutomationsActionTypes.GET_AUTOMATION_BY_NAME_ERROR,
});

export const GetAutomationDailySummaryStart = (
    params: GetAutomationDailySummaryParams
): GetAutomationDailySummaryStartAction => ({
    type: AutomationsActionTypes.GET_AUTOMATION_DAILY_SUMMARY_START,
    payload: params,
});

export const GetAutomationDailySummarySuccess = (
    singleAutomationDailySummary: JourneyDailySummary[],
    dailySummaryPagination: DailySummaryPagination
): GetAutomationDailySummarySuccessAction => ({
    type: AutomationsActionTypes.GET_AUTOMATION_DAILY_SUMMARY_SUCCESS,
    payload: {
        singleAutomationDailySummary,
        dailySummaryPagination,
    },
});

export const GetAutomationDailySummaryError = (): GetAutomationDailySummaryErrorAction => ({
    type: AutomationsActionTypes.GET_AUTOMATION_DAILY_SUMMARY_ERROR,
});

export const GetAutomationContactsStart = (params: GetAutomationContactsParams): GetAutomationContactsStartAction => ({
    type: AutomationsActionTypes.GET_AUTOMATION_CONTACTS_START,
    payload: params,
});

export const GetAutomationContactsSuccess = (
    contactsList: string[],
    contactsListPagination: ContactsListPagination,
    contactsTotalCount: number
): GetAutomationContactsSuccessAction => ({
    type: AutomationsActionTypes.GET_AUTOMATION_CONTACTS_SUCCESS,
    payload: {
        contactsList,
        contactsListPagination,
        contactsTotalCount,
    },
});

export const GetAutomationContactsError = (): GetAutomationContactsErrorAction => ({
    type: AutomationsActionTypes.GET_AUTOMATION_CONTACTS_ERROR,
});

export const LoadPreviewTemplate = (name: string): LoadPreviewTemplateAction => ({
    type: AutomationsActionTypes.LOAD_PREVIEW_TEMPLATE,
    payload: {
        name,
    },
});

export const LoadPreviewTemplateSuccess = (templates: PreviewTemplate): LoadPreviewTemplateSuccessAction => ({
    type: AutomationsActionTypes.LOAD_PREVIEW_TEMPLATE_SUCCESS,
    payload: templates,
});

export const LoadPreviewTemplateError = (): LoadPreviewTemplateErrorAction => ({
    type: AutomationsActionTypes.LOAD_PREVIEW_TEMPLATE_ERROR,
});

export const ClearPreviewTemplate = (): ClearPreviewTemplateAction => ({
    type: AutomationsActionTypes.CLEAR_PREVIEW_TEMPLATE,
    payload: null,
});

export const getAutomationStore = (state: ApplicationState): AutomationsInterface => state.automations;

export const ListFromAutomation = (params: ListFromAutomationParams): ListFromAutomationAction => ({
    type: AutomationsActionTypes.CREATE_LIST_FROM_AUTOMATION,
    payload: {
        params,
    },
});

export const ListFromAutomationSuccess = (): ListFromAutomationSuccessAction => ({
    type: AutomationsActionTypes.CREATE_LIST_FROM_AUTOMATION_SUCCESS,
});

export const ListFromAutomationError = (): ListFromAutomationErrorAction => ({
    type: AutomationsActionTypes.CREATE_LIST_FROM_AUTOMATION_ERROR,
});

export const getAutomationsCountStore = (state: ApplicationState): number => state.automations.statusCounts.total;
